import { FileText, Trash2 } from "lucide-react";

const money = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * One row per vendor quote attached to a scope: who, quote #, expiry, coverage, and the
 * Price by: Total | Unit switch. Total mode prices the quote TOTAL as a lump sum; Unit
 * mode goes back to Σ qty × unit (quoted unit where named, library otherwise). The quote
 * stays attached either way — removing it is the only way to drop it.
 */
export function ScopeQuoteStrip({ quote, colSpan, readOnly, onMode, onRemove }) {
  const mode = quote.price_mode || "total";
  const hasTotal = Number(quote.amount) > 0;
  const flags = quote.flags || [];
  return (
    <tr className="bg-sky-50/60 text-[11px] text-slate-600" data-testid={`scope-quote-${quote.id}`}>
      <td className="px-2 py-1" />
      <td colSpan={colSpan} className="px-2 py-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-1 font-medium text-sky-800">
            <FileText className="h-3 w-3" />
            Vendor quote: {quote.vendor || "—"}
            {quote.quote_ref && <span className="font-mono">#{quote.quote_ref}</span>}
          </span>
          {hasTotal && <span className="tabular-nums">TOTAL {money(quote.amount)}</span>}
          {quote.valid_through && (
            <span className={quote.expired ? "font-medium text-red-600" : ""}
                  data-testid={`scope-quote-expiry-${quote.id}`}>
              {quote.expired ? "expired" : "valid through"} {quote.valid_through}
            </span>
          )}
          {quote.coverage && <span className="text-slate-500" title={quote.coverage}>{quote.coverage}</span>}
          {flags.map((f) => (
            <span key={f} className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
                  data-testid={`scope-quote-flag-${quote.id}`}>{f}</span>
          ))}
          <span className="ml-auto inline-flex items-center gap-1.5">
            <span className="text-slate-500">Price by:</span>
            <span className="inline-flex overflow-hidden rounded border border-slate-300">
              {["total", "unit"].map((m) => (
                <button key={m} disabled={readOnly || (m === "total" && !hasTotal) || m === mode}
                        onClick={(e) => { e.stopPropagation(); onMode?.(quote, m); }}
                        title={m === "total" ? (hasTotal ? "Price the vendor TOTAL as a lump sum" : "No TOTAL on this quote")
                                             : "Price Σ qty × unit (quoted unit where named, library otherwise)"}
                        className={`px-2 py-0.5 text-[10px] font-medium capitalize ${
                          m === mode ? "bg-sky-700 text-white" : "bg-white text-slate-600 hover:bg-slate-50"} ${
                          (m === "total" && !hasTotal) ? "cursor-not-allowed opacity-40" : ""}`}
                        data-testid={`scope-quote-mode-${m}-${quote.id}`}>
                  {m}
                </button>
              ))}
            </span>
            {!readOnly && (
              <button onClick={(e) => { e.stopPropagation(); onRemove?.(quote); }}
                      className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                      title="Remove this quote — library pricing comes back"
                      data-testid={`scope-quote-remove-${quote.id}`}>
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </span>
        </div>
      </td>
    </tr>
  );
}
